/**
 * Mission-driven onboarding: each profile gets a success path for its job,
 * not a generic UI walkthrough.
 */

export type OnboardingStep = {
  readonly id: string;
  /** Matches `data-tour="…"` on the highlighted element. */
  readonly target: string;
  readonly titleKey: string;
  readonly bodyKey: string;
  /** Navigate here before highlighting (path + optional query). */
  readonly route?: string;
};

export type OnboardingMission = {
  readonly objectiveKey: string;
  readonly steps: readonly OnboardingStep[];
};

function step(role: string, id: string, target: string, route?: string): OnboardingStep {
  return {
    id,
    target,
    ...(route != null ? { route } : {}),
    titleKey: `onboarding.missions.${role}.steps.${id}.title`,
    bodyKey: `onboarding.missions.${role}.steps.${id}.body`,
  };
}

function mission(role: string, stepDefs: readonly OnboardingStep[]): OnboardingMission {
  return {
    objectiveKey: `onboarding.missions.${role}.objective`,
    steps: stepDefs,
  };
}

const BY_ROLE: Readonly<Record<string, OnboardingMission>> = {
  public: mission("public", [
    step("public", "mission", "tour-launch"),
    step("public", "cockpit", "nav-dashboard", "/dashboard"),
    step("public", "scope", "role-banner", "/dashboard"),
    step("public", "synthesis", "nav-home", "/"),
    step("public", "success", "tour-launch"),
  ]),

  viewer: mission("viewer", [
    step("viewer", "mission", "tour-launch"),
    step("viewer", "scope", "role-banner", "/dashboard"),
    step("viewer", "exploreIndicators", "nav-indicators", "/indicators"),
    step("viewer", "readMeasures", "nav-measures", "/measures"),
    step("viewer", "strategic", "nav-n1", "/n1"),
    step("viewer", "success", "tour-launch"),
  ]),

  scientist: mission("scientist", [
    step("scientist", "mission", "tour-launch"),
    step("scientist", "queue", "nav-review", "/review"),
    step("scientist", "methodCheck", "role-banner", "/dashboard"),
    step("scientist", "validateMeasures", "nav-measures", "/measures?statuses=controlee"),
    step("scientist", "qualityWatch", "nav-measures", "/measures?qualites=incertaine"),
    step("scientist", "success", "tour-launch"),
  ]),

  reviewer: mission("reviewer", [
    step("reviewer", "mission", "tour-launch"),
    step("reviewer", "openQueue", "nav-review", "/review"),
    step("reviewer", "decide", "role-banner", "/dashboard"),
    step("reviewer", "incomplete", "nav-measures", "/measures?statuses=saisie,controlee"),
    step("reviewer", "success", "tour-launch"),
  ]),

  manager: mission("manager", [
    step("manager", "mission", "tour-launch"),
    step("manager", "pilot", "nav-dashboard", "/dashboard"),
    step("manager", "publishQueue", "nav-measures", "/measures?statuses=validee"),
    step("manager", "actions", "role-banner", "/dashboard"),
    step("manager", "success", "tour-launch"),
  ]),

  admin: mission("admin", [
    step("admin", "mission", "tour-launch"),
    step("admin", "reviewPipeline", "nav-review", "/review"),
    step("admin", "publish", "nav-measures", "/measures?statuses=validee"),
    step("admin", "accounts", "nav-settings", "/settings"),
    step("admin", "success", "tour-launch"),
  ]),
};

export function onboardingMissionForRole(productLabel: string): OnboardingMission {
  return BY_ROLE[productLabel] ?? BY_ROLE.public!;
}

export function onboardingStepsForRole(productLabel: string): readonly OnboardingStep[] {
  return onboardingMissionForRole(productLabel).steps;
}

export function onboardingStorageKey(productLabel: string): string {
  return `nbs.onboarding.v2.${productLabel}`;
}
